import React, { useState } from 'react';

export const QuizExperience = ({
  backgroundColor = "#f7f5f3",
  accentColor = "#385cbc",
  textColor = "#272726"
}) => {
  const [step, setStep] = useState(0); // 0: form, 1: welcome+question, 2: results
  const [firstName, setFirstName] = useState('');
  const [condition, setCondition] = useState('');
  const [answer, setAnswer] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [animatedPoints, setAnimatedPoints] = useState(0);
  const earnedPoints = 50; // Points earned for answering

  // Sample community answers
  const communityAnswers = [
    { id: 1, text: "I immediately searched for survival rates and treatment options. I spent hours reading medical journals and patient forums trying to understand what my diagnosis meant for my future.", votes: 47, author: "Sarah M." },
    { id: 2, text: "I looked for others who had been through the same thing. Finding a community of people who understood what I was going through was more valuable than any medical information.", votes: 38, author: "Michael R." },
    { id: 3, text: "I wanted to know about lifestyle changes - diet, exercise, stress management. I needed to feel like I had some control over my health journey.", votes: 31, author: "Jennifer L." },
    { id: 4, text: "I searched for the best specialists and treatment centers. I wanted to make sure I was getting the most advanced care available.", votes: 25, author: "David K." }
  ];

  const [votes, setVotes] = useState(
    communityAnswers.reduce((acc, a) => ({ ...acc, [a.id]: a.votes }), {})
  );
  const [votedIds, setVotedIds] = useState([]);

  // Progress messages based on character count
  const getProgressMessage = (length) => {
    if (length < 50) return "Keep going! Share your experience...";
    if (length < 100) return "Great start! Tell us more...";
    if (length < 150) return "You're doing amazing! Almost there...";
    if (length < 200) return "Just a bit more detail...";
    return "Wonderful! You can submit your answer now.";
  };

  const progressPercent = Math.min((answer.length / 200) * 100, 100);
  const canSubmit = answer.length >= 200;

  // Generate confetti
  const triggerConfetti = () => {
    const pieces = [];
    const colors = ['#385cbc', '#f15b41', '#819cd1', '#fab184', '#6b8e23', '#9b59b6', '#e67e22', '#fff1b2'];

    for (let i = 0; i < 150; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 3 + Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 8 + Math.random() * 8,
        rotation: Math.random() * 360
      });
    }
    setConfettiPieces(pieces);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 5000);
  };

  const handleInitialSubmit = (e) => {
    e.preventDefault();
    if (firstName && condition) {
      triggerConfetti();
      setStep(1);
    }
  };

  const handleAnswerSubmit = () => {
    if (canSubmit) {
      setShowPointsAnimation(true);
      setAnimatedPoints(0);
      // Animate the points counting up
      let currentPoints = 0;
      const interval = setInterval(() => {
        currentPoints += 2;
        if (currentPoints >= earnedPoints) {
          setAnimatedPoints(earnedPoints);
          clearInterval(interval);
        } else {
          setAnimatedPoints(currentPoints);
        }
      }, 30);
      setStep(2);
    }
  };

  const handleVote = (id) => {
    if (!votedIds.includes(id)) {
      setVotes(prev => ({ ...prev, [id]: prev[id] + 1 }));
      setVotedIds(prev => [...prev, id]);
      // Add 5 bonus points for voting
      setAnimatedPoints(prev => prev + 5);
    }
  };

  // Step 0: Initial form
  if (step === 0) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div
          style={{
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '700',
              color: textColor,
              marginBottom: '16px'
            }}
          >
            Let's get started
          </h1>
          <p
            style={{
              fontSize: '18px',
              color: textColor,
              opacity: 0.7,
              marginBottom: '24px'
            }}
          >
            Tell us a little about yourself
          </p>

          {/* Demo notice */}
          <div
            style={{
              backgroundColor: `${accentColor}08`,
              border: `1px solid ${accentColor}20`,
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '32px',
              textAlign: 'left'
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: textColor,
                margin: '0 0 12px 0',
                lineHeight: 1.5
              }}
            >
              <strong style={{ color: accentColor }}>This is a demo experience.</strong> Your information is not saved or stored anywhere.
            </p>
            <p
              style={{
                fontSize: '14px',
                color: textColor,
                opacity: 0.7,
                margin: 0,
                lineHeight: 1.5
              }}
            >
              When you continue, you'll see an example of the types of questions we ask our community, then discover how other patients answered.
            </p>
          </div>

          <form onSubmit={handleInitialSubmit}>
            <div style={{ marginBottom: '24px', textAlign: 'left' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: textColor,
                  marginBottom: '8px'
                }}
              >
                What's your first name?
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '18px',
                  border: `2px solid ${textColor}20`,
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = accentColor}
                onBlur={(e) => e.target.style.borderColor = `${textColor}20`}
              />
            </div>

            <div style={{ marginBottom: '40px', textAlign: 'left' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: textColor,
                  marginBottom: '8px'
                }}
              >
                What condition have you been diagnosed with?
              </label>
              <input
                type="text"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                placeholder="e.g., Diabetes, Cancer, Lupus..."
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '18px',
                  border: `2px solid ${textColor}20`,
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = accentColor}
                onBlur={(e) => e.target.style.borderColor = `${textColor}20`}
              />
            </div>

            <button
              type="submit"
              disabled={!firstName || !condition}
              style={{
                width: '100%',
                padding: '18px 40px',
                fontSize: '18px',
                fontWeight: '700',
                backgroundColor: firstName && condition ? accentColor : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: firstName && condition ? 'pointer' : 'not-allowed',
                transition: 'transform 0.2s, background-color 0.2s'
              }}
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 1: Welcome + Question
  if (step === 1) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColor,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Confetti */}
        {showConfetti && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1000 }}>
            <style>
              {`
                @keyframes confettiFall {
                  0% { transform: translateY(-100px) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
              `}
            </style>
            {confettiPieces.map((piece) => (
              <div
                key={piece.id}
                style={{
                  position: 'absolute',
                  left: `${piece.left}%`,
                  top: '-20px',
                  width: `${piece.size}px`,
                  height: `${piece.size}px`,
                  backgroundColor: piece.color,
                  borderRadius: piece.id % 3 === 0 ? '50%' : '2px',
                  animation: `confettiFall ${piece.duration}s ease-out ${piece.delay}s forwards`,
                  transform: `rotate(${piece.rotation}deg)`
                }}
              />
            ))}
          </div>
        )}

        <div
          style={{
            maxWidth: '640px',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(36px, 6vw, 56px)',
              fontWeight: '700',
              color: textColor,
              marginBottom: '16px'
            }}
          >
            Welcome, {firstName}!
          </h1>
          <p
            style={{
              fontSize: '20px',
              color: textColor,
              opacity: 0.7,
              marginBottom: '48px'
            }}
          >
            See an example of the types of questions we ask below.
          </p>

          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              textAlign: 'left'
            }}
          >
            <p
              style={{
                fontSize: '22px',
                fontWeight: '600',
                color: textColor,
                marginBottom: '24px',
                lineHeight: '1.4'
              }}
            >
              When you were first diagnosed with {condition}, what information did you seek?
            </p>

            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Share your experience..."
              style={{
                width: '100%',
                minHeight: '160px',
                padding: '20px',
                fontSize: '16px',
                border: `2px solid ${textColor}20`,
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = accentColor}
              onBlur={(e) => e.target.style.borderColor = `${textColor}20`}
            />

            {/* Progress bar */}
            <div style={{ marginTop: '20px' }}>
              <div
                style={{
                  height: '8px',
                  backgroundColor: `${textColor}15`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${progressPercent}%`,
                    backgroundColor: canSubmit ? '#6b8e23' : accentColor,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease, background-color 0.3s'
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: '14px',
                  color: canSubmit ? '#6b8e23' : textColor,
                  fontWeight: canSubmit ? '600' : '400',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {canSubmit && <span style={{ fontSize: '18px' }}>✨</span>}
                {getProgressMessage(answer.length)}
                <span style={{ marginLeft: 'auto', opacity: 0.5 }}>
                  {answer.length}/200
                </span>
              </p>
            </div>

            <button
              onClick={handleAnswerSubmit}
              disabled={!canSubmit}
              style={{
                marginTop: '24px',
                width: '100%',
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '700',
                backgroundColor: canSubmit ? accentColor : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'transform 0.2s, background-color 0.2s'
              }}
            >
              Submit Answer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Community answers
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: backgroundColor,
        padding: '60px 20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 600px) {
          .quiz-cta-section {
            padding: 32px 24px !important;
          }
          .quiz-stats-container {
            gap: 24px !important;
          }
          .quiz-community-card {
            flex-direction: column !important;
            gap: 16px !important;
          }
          .quiz-vote-section {
            flex-direction: row !important;
            width: 100% !important;
            justify-content: flex-start !important;
          }
          .quiz-features-list {
            flex-direction: column !important;
            gap: 8px !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
      <div
        style={{
          maxWidth: '720px',
          margin: '0 auto'
        }}
      >
        {/* Points Celebration */}
        {showPointsAnimation && (
          <div
            style={{
              backgroundColor: 'linear-gradient(135deg, #fff9e6 0%, #fff1b2 100%)',
              background: 'linear-gradient(135deg, #fff9e6 0%, #fff1b2 100%)',
              border: '2px solid #f5c518',
              borderRadius: '16px',
              padding: '20px 28px',
              marginBottom: '32px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Sparkle effects */}
            <style>
              {`
                @keyframes sparkle {
                  0%, 100% { opacity: 0; transform: scale(0); }
                  50% { opacity: 1; transform: scale(1); }
                }
                @keyframes bounce {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-8px); }
                }
              `}
            </style>
            <div style={{ position: 'absolute', top: '10px', left: '15%', fontSize: '20px', animation: 'sparkle 1.5s ease-in-out infinite 0.2s' }}>✨</div>
            <div style={{ position: 'absolute', top: '15px', right: '20%', fontSize: '16px', animation: 'sparkle 1.5s ease-in-out infinite 0.5s' }}>⭐</div>
            <div style={{ position: 'absolute', bottom: '10px', left: '25%', fontSize: '14px', animation: 'sparkle 1.5s ease-in-out infinite 0.8s' }}>✨</div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '32px', animation: 'bounce 0.6s ease-in-out infinite' }}>🎉</span>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#996b00', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  You earned
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#b8860b', lineHeight: 1 }}>
                  +{animatedPoints} pts
                </div>
              </div>
              <span style={{ fontSize: '32px', animation: 'bounce 0.6s ease-in-out infinite 0.3s' }}>🎉</span>
            </div>
            <p style={{ fontSize: '13px', color: '#996b00', margin: 0, opacity: 0.8 }}>
              <em>Demo points for illustration. Earn points for sharing your experience in the app, plus get paid $75-$500 for research studies!</em>
            </p>
          </div>
        )}

        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              backgroundColor: '#6b8e23',
              borderRadius: '50%',
              marginBottom: '20px'
            }}
          >
            <span style={{ fontSize: '28px', color: '#fff' }}>✓</span>
          </div>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(28px, 5vw, 40px)',
              fontWeight: '700',
              color: textColor,
              marginBottom: '12px'
            }}
          >
            Thank you for sharing, {firstName}!
          </h1>
          <p
            style={{
              fontSize: '18px',
              color: textColor,
              opacity: 0.7
            }}
          >
            See how others answered the same question. Upvote answers that resonate with you.
          </p>
        </div>

        {/* User's answer */}
        <div
          style={{
            backgroundColor: `${accentColor}10`,
            border: `2px solid ${accentColor}`,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span
              style={{
                backgroundColor: accentColor,
                color: '#fff',
                fontSize: '12px',
                fontWeight: '600',
                padding: '4px 10px',
                borderRadius: '20px'
              }}
            >
              YOUR ANSWER
            </span>
          </div>
          <p style={{ fontSize: '16px', color: textColor, lineHeight: '1.6', margin: 0 }}>
            {answer}
          </p>
        </div>

        <h2
          style={{
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: textColor,
            opacity: 0.5,
            marginBottom: '20px'
          }}
        >
          Community Answers
        </h2>

        {/* Community answers */}
        {communityAnswers.map((item) => (
          <div
            key={item.id}
            className="quiz-community-card"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              display: 'flex',
              gap: '20px'
            }}
          >
            <div
              className="quiz-vote-section"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <button
                onClick={() => handleVote(item.id)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  border: votedIds.includes(item.id) ? `2px solid ${accentColor}` : '2px solid #e0e0e0',
                  backgroundColor: votedIds.includes(item.id) ? `${accentColor}10` : '#fff',
                  cursor: votedIds.includes(item.id) ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                <span style={{ fontSize: '20px' }}>
                  {votedIds.includes(item.id) ? '💙' : '👆'}
                </span>
                {!votedIds.includes(item.id) && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      backgroundColor: '#f5c518',
                      color: '#996b00',
                      fontSize: '9px',
                      fontWeight: '700',
                      padding: '2px 5px',
                      borderRadius: '10px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    +5 pts
                  </span>
                )}
              </button>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: votedIds.includes(item.id) ? accentColor : textColor
                }}
              >
                {votes[item.id]}
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '16px', color: textColor, lineHeight: '1.6', margin: '0 0 12px 0' }}>
                {item.text}
              </p>
              <span style={{ fontSize: '14px', color: textColor, opacity: 0.5 }}>
                — {item.author}
              </span>
            </div>
          </div>
        ))}

        {/* Join CTA Section */}
        <div
          className="quiz-cta-section"
          style={{
            marginTop: '60px',
            padding: '48px 40px',
            background: `linear-gradient(135deg, ${accentColor} 0%, #5a7dd4 50%, #819cd1 100%)`,
            borderRadius: '24px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative elements */}
          <div
            style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              pointerEvents: 'none'
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              pointerEvents: 'none'
            }}
          />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 16px',
                borderRadius: '20px',
                marginBottom: '20px'
              }}
            >
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                You're not alone, {firstName}
              </span>
            </div>

            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(28px, 5vw, 38px)',
                fontWeight: '700',
                color: '#fff',
                marginTop: 0,
                marginBottom: '16px',
                lineHeight: 1.2
              }}
            >
              Your story has the power<br />to change healthcare
            </h2>

            <p
              style={{
                fontSize: '17px',
                color: 'rgba(255,255,255,0.9)',
                maxWidth: '480px',
                margin: '0 auto 32px',
                lineHeight: 1.6
              }}
            >
              Join thousands of patients sharing their real experiences.
              Together, we're building the insights that help others navigate
              their journey—and pushing healthcare forward.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => window.location.href = 'https://apply.savvy.coop/register'}
                style={{
                  padding: '18px 48px',
                  fontSize: '18px',
                  fontWeight: '700',
                  backgroundColor: '#fff',
                  color: accentColor,
                  border: 'none',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                }}
              >
                Join the Community
              </button>

              <p
                style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.7)',
                  margin: 0
                }}
              >
                Free to join • Takes 2 minutes • Make an impact
              </p>
            </div>

            {/* Social proof */}
            <div
              className="quiz-stats-container"
              style={{
                marginTop: '36px',
                paddingTop: '28px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                justifyContent: 'center',
                gap: '40px',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>12,000+</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Patient stories</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>50+</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Conditions</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>100%</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Patient-driven</div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary CTA - More personal with rewards emphasis */}
        <div
          style={{
            marginTop: '32px',
            padding: '32px',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            textAlign: 'center'
          }}
        >
          {/* Points summary */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#fff9e6',
              border: '1px solid #f5c518',
              borderRadius: '20px',
              padding: '8px 16px',
              marginBottom: '20px'
            }}
          >
            <span style={{ fontSize: '18px' }}>🏆</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#996b00' }}>
              Demo total: {animatedPoints} pts
            </span>
          </div>

          <p
            style={{
              fontSize: '16px',
              color: textColor,
              opacity: 0.8,
              margin: '0 0 16px 0',
              lineHeight: 1.6
            }}
          >
            <strong>What happens when you join?</strong> You'll answer more questions like this one,
            see insights from your specific condition, and help researchers and healthcare providers
            understand what patients really need.
          </p>

          <p
            style={{
              fontSize: '15px',
              color: '#996b00',
              backgroundColor: '#fff9e6',
              padding: '12px 16px',
              borderRadius: '8px',
              margin: '0 0 20px 0',
              lineHeight: 1.5
            }}
          >
            <strong>Two ways to earn:</strong> Earn points for sharing your experience in the app—redeem for gift cards or donate to health charities.
            Plus, get paid real dollars ($75-$500) when you participate in research studies, interviews, and focus groups.
          </p>

          <div
            className="quiz-features-list"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '32px',
              flexWrap: 'wrap',
              fontSize: '14px',
              color: textColor,
              opacity: 0.6
            }}
          >
            <span>✓ 5-10 min/week</span>
            <span>✓ 100% anonymous option</span>
            <span>✓ Points + paid research</span>
          </div>
        </div>
      </div>
    </div>
  );
};
